// src/components/AttributeDropdown/AttributeDropdown.tsx
import React from 'react';

import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import { type AttributeMapping } from '../../constants/attributeConstants';
import { type AttributeField } from '../../data-layer/types';

import './AttributeDropdown.css';

interface DropdownProps {
  rowId: string;
  field: 'serviceNowAttributeName' | 'saviyntAttributeName';
  mappings: AttributeMapping[];
  options: string[];
  onSelect: (
    id: string,
    value: string,
    field: 'serviceNowAttributeName' | 'saviyntAttributeName'
  ) => void;
  searchText: string;
  setSearchText: (val: string) => void;
  dropdownDirection: 'up' | 'down';
}

/**
 * AttributeDropdown is a controlled dropdown component with search,
 * filtering, and exclusion of already-selected attribute options.
 */
const AttributeDropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      rowId,
      field,
      mappings,
      options,
      onSelect,
      searchText,
      setSearchText,
      dropdownDirection,
    },
    ref
  ) => {
    const formatMessage = useLocalization();

    // Collect already used values from mappings to exclude them in dropdown
    const usedValues = new Set(mappings.map((m) => m[field as AttributeField]));

    // Filter options to exclude used ones and match current search input
    const filtered = options
      .filter((opt) => !usedValues.has(opt))
      .filter((opt) => opt.toLowerCase().includes(searchText.toLowerCase()));

    // Custom suffix localized
    const customSuffix = formatMessage(
      'INTEGRATIONS.ATTRIBUTE_DROPDOWN.CUSTOM_SUFFIX'
    );

    // If user types a value that’s not in options (for ServiceNow field only),
    // allow adding it as a custom option
    if (
      field === 'serviceNowAttributeName' &&
      searchText &&
      !options.includes(searchText)
    ) {
      filtered.push(`${searchText} ${customSuffix}`);
    }

    // Determine placeholder key based on field
    const placeholderKey =
      field === 'serviceNowAttributeName'
        ? 'INTEGRATIONS.ATTRIBUTE_DROPDOWN.SEARCH_SERVICENOW'
        : 'INTEGRATIONS.ATTRIBUTE_DROPDOWN.SEARCH_SAVIYNT';

    return (
      <div
        ref={ref}
        className={`attribute-name-dropdown ${
          dropdownDirection === 'up' ? 'open-up' : 'open-down'
        }`}
        data-testid={testIds.ATTRIBUTE_DROPDOWN}>
        {/* Search input box for filtering dropdown options */}
        <input
          className='attribute-name-search'
          placeholder={formatMessage(placeholderKey)}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          data-testid={testIds.ATTRIBUTE_DROPDOWN_SEARCH}
        />

        {/* Render filtered and selectable options as a list */}
        <ul
          className='attribute-name-options'
          data-testid={testIds.ATTRIBUTE_DROPDOWN_OPTIONS}>
          {filtered.map((option) => (
            <li key={option}>
              <button
                type='button'
                className='attribute-name-option'
                data-testid={testIds.ATTRIBUTE_DROPDOWN_OPTION}
                onClick={() => {
                  onSelect(
                    rowId,
                    option.replace(` ${customSuffix}`, ''),
                    field
                  );
                }}>
                {option}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }
);

AttributeDropdown.displayName = 'AttributeDropdown';

export default AttributeDropdown;
